import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { save } from "./api-atividade";
import { setAtividade as setAtividadeRedux } from "./actions/actions";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../util/ToastUtil";
import SunEditorUI from "../UI/SunEditorUI";
import TextFieldDate from "../UI/TextFieldDate";
import TextFieldDateTime from "../UI/TextFieldDateTime";

const Opcao = ({ label, description, checked, onChange }) => (
  <FormControlLabel
    sx={{ alignItems: "flex-start", m: 0 }}
    control={<Switch checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />}
    label={
      <Box pt={0.75}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
    }
  />
);

export default function AtividadeForm() {
  const dispatch = useDispatch();
  const atividadeRedux = useSelector((state) => state.atividade);
  const [atividade, setAtividade] = useState(atividadeRedux);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setAtividade(atividadeRedux);
  }, [atividadeRedux]);

  if (!atividade) return null;
  const eNovo = !atividade.codigo;
  const fechar = () => dispatch(setAtividadeRedux(null));

  const updateField = (name, value) => {
    setAtividade((prevState) => ({ ...prevState, [name]: value }));
  };

  const clickSubmit = (event) => {
    event.preventDefault();
    if (!atividade.data) {
      errorToast("Informe a data da atividade.");
      return;
    }
    if (atividade.entrega && !atividade.dataLimiteEntrega) {
      errorToast("Informe a data limite de entrega.");
      return;
    }
    setIsSaving(true);
    save(atividade)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        success("Salvo com sucesso.");
        // A lista é recarregada ao voltar, já com a atividade salva.
        fechar();
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
        <Tooltip title="Voltar para a lista">
          <IconButton edge="start" onClick={fechar}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle1" fontWeight={600}>
          {eNovo ? "Nova Atividade" : "Editar Atividade"}
        </Typography>
      </Stack>

      <Stack component="form" onSubmit={clickSubmit} spacing={2.5}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextFieldDate
              label="Data"
              value={atividade.data || null}
              onChange={(value) => updateField("data", value ? dayjs(value) : null)}
              slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 8, md: 9 }}>
            <TextField
              size="small"
              fullWidth
              required
              label="Descrição"
              value={atividade.descricao ?? ""}
              onChange={(event) => updateField("descricao", event.target.value)}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              helperText={`${(atividade.descricao ?? "").length}/100`}
            />
          </Grid>
        </Grid>

        <Box>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Conteúdo
          </Typography>
          <SunEditorUI
            onChange={(value) => updateField("conteudo", value.replace(/[​-‍﻿]/g, ""))}
            content={atividade.conteudo ?? ""}
          />
        </Box>

        <Divider />

        <Stack spacing={1.5}>
          <Opcao
            label="Sem nota"
            description="A atividade não recebe nota dos alunos."
            checked={atividade.semNota}
            onChange={(value) => updateField("semNota", value)}
          />
          <Opcao
            label="Entrega"
            description="Os alunos devem entregar a atividade até a data limite."
            checked={atividade.entrega}
            onChange={(value) => updateField("entrega", value)}
          />
          {atividade.entrega && (
            <Box pl={{ sm: 7 }}>
              <TextFieldDateTime
                label="Data Limite Entrega"
                value={atividade.dataLimiteEntrega || null}
                onChange={(value) => updateField("dataLimiteEntrega", value ? dayjs(value) : null)}
                slotProps={{ textField: { size: "small", required: true } }}
              />
            </Box>
          )}
        </Stack>

        <Box>
          <Button
            type="submit"
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
            disabled={isSaving}
          >
            Salvar
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
